/**
 * The transcript (plan docs/plans/lms-plan.md 7.5, 10.1, 10.4; R24; WP-41):
 * every completion the person holds, current and superseded (a superseded
 * completion keeps its certificate, 10.1), newest first, with course,
 * version, kind, completed on, expires on, score, state, the certificate
 * code and Download (components/CertificateRow).
 *
 * GET /me/completions (WP-40's api/completions.ts listMyCompletions; WP-34
 * transcriptItem: state is 'current', 'expired' or 'superseded'), a page at a
 * time with "Load more" (Appendix A: a page is at most 200 rows; 2.3a: never
 * one unbounded read).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import { outlineBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { apiErrorMessage } from '../../api/client';
import { listMyCompletions } from '../../api/completions';
import type { TranscriptItem } from '../../api/completions';
import type { LmsList } from '../../api/client';
import { CertificateRow } from '../../components/CertificateRow';
import { formatDay } from '../../components/DueBadge';
import { Pill } from '../../components/StatusPill';
import type { PillTone } from '../../components/StatusPill';
import { COMPLETION_KIND_WORDS } from '../home/RecentList';
import { Section, SectionError, SectionSkeleton } from '../home/Section';
import { coursePath } from '../home/homeData';
import { useLoad } from '../home/useLoad';

/** Rows per transcript page (Appendix A: at most 200). */
export const TRANSCRIPT_PAGE = 50;

const STATE_PILL: Readonly<Record<string, { tone: PillTone; word: string }>> = {
  current: { tone: 'green', word: 'Current' },
  expired: { tone: 'red', word: 'Expired' },
  superseded: { tone: 'gray', word: 'Superseded' },
};

/** 5.5 superseded_reason in words. */
const SUPERSEDED_WORDS: Readonly<Record<string, string>> = {
  renewed: 'Renewed by a later completion',
  new_version_immediate: 'Retrained on a new version',
  new_version_acknowledge: 'A new version was acknowledged',
  external_replaced: 'Replaced by a newer external certificate',
};

const thCls = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
const tdCls = 'px-4 py-3 text-sm text-gray-900 dark:text-gray-100';

export function TranscriptTable({ reloadKey }: { reloadKey: number }) {
  const { toast } = useToast();
  const first = useLoad<LmsList<TranscriptItem>>(
    () => listMyCompletions({ limit: TRANSCRIPT_PAGE, offset: 0 }),
    'Your transcript could not be loaded.',
    reloadKey
  );
  const [more, setMore] = useState<TranscriptItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // A fresh first page starts the list again.
  useEffect(() => {
    setMore([]);
    setHasMore(first.data?.page?.hasMore === true);
  }, [first.data]);

  const rows = [...(first.data?.items ?? []), ...more];

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listMyCompletions({ limit: TRANSCRIPT_PAGE, offset: rows.length });
      const seen = new Set(rows.map((r) => r.id));
      setMore((prev) => prev.concat((page?.items ?? []).filter((r) => !seen.has(r.id))));
      setHasMore(page?.page?.hasMore === true);
    } catch (err) {
      toast(apiErrorMessage(err, 'More of your transcript could not be loaded.'), { type: 'error' });
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Section
      title="Transcript"
      icon={Award}
      count={first.data ? rows.length : null}
      headingId="lms-records-transcript-title"
      data-testid="lms-transcript"
    >
      {first.loading ? (
        <SectionSkeleton data-testid="lms-transcript-loading" />
      ) : first.error ? (
        <SectionError message={first.error} onRetry={first.reload} data-testid="lms-transcript-error" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Award}
          title="No completions yet"
          hint="Every training you complete is kept here with its certificate, including the ones a later completion replaces."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-transcript-table">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th scope="col" className={thCls}>Course</th>
                <th scope="col" className={thCls}>Version</th>
                <th scope="col" className={thCls}>Kind</th>
                <th scope="col" className={thCls}>Completed</th>
                <th scope="col" className={thCls}>Expires</th>
                <th scope="col" className={thCls}>Score</th>
                <th scope="col" className={thCls}>Status</th>
                <th scope="col" className={thCls}>Certificate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((c) => {
                const title = c.course?.title ?? 'Course';
                const pill = STATE_PILL[c.state] ?? { tone: 'gray' as PillTone, word: c.state };
                const muted = c.state === 'superseded';
                return (
                  <tr
                    key={c.id}
                    className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    data-testid="lms-transcript-row"
                    data-state={c.state}
                  >
                    <td className={tdCls}>
                      <Link
                        to={coursePath(c.course_id)}
                        className={`font-medium hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-primary-400 ${
                          muted ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {title}
                      </Link>
                    </td>
                    <td className={`${tdCls} whitespace-nowrap tabular-nums`}>{c.version_no ? `v${c.version_no}` : ''}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{COMPLETION_KIND_WORDS[c.kind] ?? c.kind}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{formatDay(c.completed_at)}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{c.expires_at ? formatDay(c.expires_at) : 'Does not expire'}</td>
                    <td className={`${tdCls} whitespace-nowrap tabular-nums`}>{typeof c.score_pct === 'number' ? `${c.score_pct}%` : ''}</td>
                    <td className={tdCls}>
                      <Pill
                        tone={pill.tone}
                        title={muted && c.superseded_reason ? SUPERSEDED_WORDS[c.superseded_reason] ?? undefined : undefined}
                        data-testid="lms-transcript-state"
                      >
                        {pill.word}
                      </Pill>
                      {muted && c.superseded_at ? (
                        <div className="mt-1 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                          {`${SUPERSEDED_WORDS[c.superseded_reason ?? ''] ?? 'Superseded'} on ${formatDay(c.superseded_at)}`}
                        </div>
                      ) : null}
                    </td>
                    <td className={tdCls}>
                      <CertificateRow
                        completionId={c.id}
                        kind={c.kind}
                        category={c.course?.category ?? null}
                        certificate={c.certificate}
                        courseTitle={title}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!first.loading && !first.error && hasMore ? (
        <div className="border-t border-gray-200 px-4 py-2 dark:border-gray-700">
          <button type="button" className={outlineBtnCls()} onClick={() => void loadMore()} disabled={loadingMore} data-testid="lms-transcript-more">
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      ) : null}
    </Section>
  );
}

export default TranscriptTable;
